import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getContacts, deleteContact, convertContactToLead, syncVirtualContact, inviteContactToPortal } from '../../../api/contact';
import { User, Mail, Phone, Plus, Edit, Trash2, UserPlus, Search, Filter, Upload, ChevronRight, Send, Check, SlidersHorizontal, ArrowUpDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiCache, cacheKeys, cachedApiCall } from '../../../lib/cache';
import { importContactsCsv, exportContactsVaultCsv } from '../../../api/contacts';
import ContactNotesButton from '../components/ContactNotesButton';
import BuyerPreferencesModal from '../components/BuyerPreferencesModal';
import BulkAddBuyersModal from '../components/BulkAddBuyersModal';
import RecentContactImports from '../components/RecentContactImports';
import DataCheckPanel from '../../../components/import/DataCheckPanel';
import ListKindPicker from '../../../components/import/ListKindPicker';
import InviteAllDialog from '../components/InviteAllDialog';
import { hasAdminAccess } from "../../../utils/roleAccess";

const SOURCE_LABELS = {
  buyer_portal: 'User Portal',
  website: 'Website',
  referral: 'Referral',
  ofi: 'Open For Inspection',
  social_media: 'Social Media',
  cold_call: 'Cold Call',
  walk_in: 'Walk In',
  other: 'Other',
  rea: 'REA',
  domain: 'Domain',
};
const formatSource = (src) =>
  SOURCE_LABELS[src] || (src || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const ContactList = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const isAdmin = hasAdminAccess(user);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    contact_type: '',
    source: '',
    is_lead: '',
    is_buyer_advocate: '', // '' = all, 'true' = buyer advocates only
    portal_status: '', // '' = all, 'invited' = has account/invited, 'not_invited' = neither
    ordering: '' // '' = default, 'name' = A-Z, '-name' = Z-A
  });
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 8,
    total_pages: 1
  });
  const [analytics, setAnalytics] = useState({
    total_contacts: 0,
    with_portal_account: 0,
    eligible_for_portal_invite: 0,
    buyers: 0,
    sellers: 0
  });
  const [searchTerm, setSearchTerm] = useState(filters.search);
  // Contact whose buying preferences are being edited (null = closed).
  const [prefsContact, setPrefsContact] = useState(null);

  // The old filter bar had three separate dropdowns for "what kind of contact"
  // (contact type, leads vs contacts, buyers advocates). They are now one
  // "All Types" dropdown; these two helpers translate between that single
  // <select> value and the three query params the API still expects.
  const typeFilterValue =
    filters.is_buyer_advocate === 'true' ? 'buyer_advocates'
      : filters.is_lead === 'true' ? 'leads_only'
        : filters.is_lead === 'false' ? 'contacts_only'
          : filters.contact_type ? `type:${filters.contact_type}`
            : '';

  const applyTypeFilter = (value) => {
    setFilters({
      ...filters,
      contact_type: value.startsWith('type:') ? value.slice(5) : '',
      is_lead: value === 'leads_only' ? 'true' : value === 'contacts_only' ? 'false' : '',
      is_buyer_advocate: value === 'buyer_advocates' ? 'true' : ''
    });
  };

  // Check if a contact is a virtual contact (buyer or enquiry)
  const isVirtualContact = (contact) => {
    if (!contact || !contact.id) return false;
    const id = String(contact.id);
    return id.startsWith('buyer_') || id.startsWith('enquiry_');
  };

  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Filters chosen inside the Export dialog (independent of the page filters,
  // but seeded from them when the dialog opens).
  const [exportFilters, setExportFilters] = useState({
    contact_type: '',
    source: '',
    is_lead: '',
    is_buyer_advocate: '',
  });

  const openExportModal = () => {
    // Seed the dialog with whatever the user is currently filtering by.
    setExportFilters({
      contact_type: filters.contact_type || '',
      source: filters.source || '',
      is_lead: filters.is_lead || '',
      is_buyer_advocate: filters.is_buyer_advocate || '',
    });
    setShowExportModal(true);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // Drop empty values so the backend treats them as "no filter".
      const params = Object.fromEntries(
        Object.entries(exportFilters).filter(([, v]) => v !== '' && v != null)
      );
      const res = await exportContactsVaultCsv(params);
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const typeTag = exportFilters.contact_type ? `_${exportFilters.contact_type}` : '';
      link.setAttribute('download', `contacts_vault_export${typeTag}_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export started');
      setShowExportModal(false);
    } catch (e) {
      console.error(e);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const [showBulkAddBuyers, setShowBulkAddBuyers] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState({ file: null, duplicate_strategy: 'skip', assign_to_me: true, list_kind: '' });
  const [importing, setImporting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  // Dry-run preview returned by the backend before committing the import.
  const [importPreview, setImportPreview] = useState(null);

  const buildImportForm = (overrides = {}) => {
    const form = new FormData();
    form.append('file', importData.file);
    form.append('duplicate_strategy', importData.duplicate_strategy);
    form.append('assign_to_me', String(importData.assign_to_me));
    if (importData.list_kind) form.append('list_kind', importData.list_kind);
    Object.entries(overrides).forEach(([k, v]) => form.append(k, String(v)));
    return form;
  };

  const resetImportModal = () => {
    setShowImportModal(false);
    setImportPreview(null);
    setImportData({ file: null, duplicate_strategy: 'skip', assign_to_me: true, list_kind: '' });
  };

  // Step 1: ask the backend to parse + analyse the file without writing anything.
  const handleAnalyzeImport = async () => {
    if (!importData.file) return;
    setAnalyzing(true);
    try {
      const res = await importContactsCsv(buildImportForm({ dry_run: 'true' }));
      setImportPreview(res.data);
    } catch (e) {
      const data = e?.response?.data || {};
      const detail = data.error || data.detail || e?.message || 'Could not read the file';
      toast.error(detail, { duration: 6000 });
      if (data.detected_headers) {
        console.error('Import, detected headers:', data.detected_headers);
        console.error('Import, expected headers:', data.expected_headers);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  // Step 2: commit the import for real once the user has reviewed the preview.
  const handleConfirmImport = async () => {
    if (!importData.file) return;
    setImporting(true);
    try {
      const res = await importContactsCsv(buildImportForm());
      const s = res.data?.summary || {};
      const jobId = res.data?.import_job_id;
      const rowErrors = res.data?.errors || [];
      if (rowErrors.length > 0) {
        console.warn('Import row errors:', rowErrors);
      }
      const errSuffix = s.errors_count > 0 ? `, ${s.errors_count} errors (see console)` : '';
      toast.success(
        `Imported: ${s.created} created, ${s.updated} updated, ${s.skipped} skipped${errSuffix}.`
        + (jobId && s.created ? ' You can undo it from Import > Recent imports.' : ''),
        { duration: 6000 },
      );
      resetImportModal();
      apiCache.invalidatePattern('contacts-');
      loadContacts(true);
    } catch (e) {
      const data = e?.response?.data || {};
      const detail = data.error || data.detail || e?.message || 'Import failed';
      toast.error(detail, { duration: 6000 });
      // The server re-checks the data on commit; show what it found.
      if (data.quality) setImportPreview((prev) => (prev ? { ...prev, quality: data.quality } : prev));
      console.error('Import error:', data || e);
    } finally {
      setImporting(false);
    }
  };
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [contactToConvert, setContactToConvert] = useState(null);
  const [showInviteAllModal, setShowInviteAllModal] = useState(false);
  const [invitingId, setInvitingId] = useState(null);
  const [invitedIds, setInvitedIds] = useState(() => new Set());
  const hasLoadedRef = useRef(false);
  const observerTarget = useRef(null);

  // Debounce search effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchTerm }));
    }, 400); // 400ms debounce is usually a good middle ground
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Consolidated load effect for initial load, page changes, and filter changes
  useEffect(() => {
    // Initial load
    if (!hasLoadedRef.current) {
      apiCache.invalidatePattern('contacts-');
      loadContacts(true);
      hasLoadedRef.current = true;
      return; // Exit after initial load
    }

    // On page change or filter change
    loadContacts(false);
  }, [filters, pagination.page]);

  // Reset page when filters change - this will trigger the effect above if page was > 1
  // If page was already 1, the effect above will still trigger because 'filters' changed
  const lastFiltersRef = useRef(filters);
  useEffect(() => {
    if (lastFiltersRef.current !== filters) {
      lastFiltersRef.current = filters;
      if (pagination.page !== 1) {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }
  }, [filters]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !loading && !isRefreshing && pagination.page < pagination.total_pages) {
          console.log('[Contacts] Bottom reached, loading next page:', pagination.page + 1);
          setPagination(prev => ({ ...prev, page: prev.page + 1 }));
        }
      },
      { threshold: 1.0 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [loading, isRefreshing, pagination.page, pagination.total_pages]);

  // Reset page when filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [filters]);

  // Refetch contacts when user returns to tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && hasLoadedRef.current) {
        apiCache.invalidatePattern('contacts-');
        loadContacts(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [filters, pagination.page_size]); // Added page_size to handle potential changes

  const loadContacts = async (forceRefresh = false) => {
    try {
      // Use full page loading only for initial mount or force refresh
      // Otherwise use the non-intrusive isRefreshing state
      const isInitialLoad = !hasLoadedRef.current;

      if (isInitialLoad || forceRefresh) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }

      const apiParams = {
        ...filters,
        page: pagination.page,
        page_size: pagination.page_size
      };

      console.log('[Contacts] Loading contacts with params:', apiParams, 'forceRefresh:', forceRefresh);

      // Use enhanced caching with stale-while-revalidate
      const response = await cachedApiCall(
        cacheKeys.contacts(apiParams), // Include page/page_size in cache key
        () => getContacts(apiParams),
        10 * 60 * 1000, // 10 minute cache
        { forceRefresh }
      );

      // Handle response format
      let allContacts = [];
      let totalCount = 0;
      let totalPages = 1;
      let currentPage = pagination.page;

      const data = response?.data || response;

      if (data && typeof data === 'object' && 'results' in data) {
        // Paginated response
        allContacts = data.results;
        totalCount = data.count || 0;
        totalPages = data.total_pages || 1;
        currentPage = data.page || currentPage;
      } else if (Array.isArray(data)) {
        // Non-paginated response
        allContacts = data;
        console.log(`[Contacts] Loaded ${allContacts.length} contacts, total ${totalCount}`);
      } else {
        allContacts = [];
      }


      setContacts(prev => pagination.page === 1 ? allContacts : [...prev, ...allContacts]);
      setPagination(prev => ({
        ...prev,
        count: totalCount,
        total_pages: totalPages,
        page: currentPage
      }));
      if (data && data.analytics) {
        setAnalytics(data.analytics);
      }
      return allContacts;
    } catch (error) {
      console.error('[Contacts] Error loading contacts:', error);
      console.error('[Contacts] Error details:', error.response?.data || error.message);
      setContacts([]);
      return [];
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleDelete = (id) => {
    const contact = contacts.find(c => c.id === id);
    setContactToDelete(contact);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!contactToDelete) return;

    const deletedId = contactToDelete.id;
    const deletedEmail = (contactToDelete.email || '').toLowerCase().trim();

    try {
      await deleteContact(deletedId);

      // Remove from list immediately: the deleted contact + any Portal Synced row for same email.
      // Do NOT refetch here. Refetch would bring the same person back as a portal buyer.
      setContacts((prev) =>
        prev.filter((c) => {
          if (c.id === deletedId) return false;
          if (deletedEmail && isVirtualContact(c)) {
            const email = (c.email || '').toLowerCase().trim();
            if (email === deletedEmail) return false;
          }
          return true;
        })
      );

      apiCache.invalidatePattern('contacts-');
      toast.success('Contact deleted successfully');
      setShowDeleteModal(false);
      setContactToDelete(null);
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  const handleConvertToLead = (contact) => {
    setContactToConvert(contact);
    setShowConvertModal(true);
  };

  const confirmConvertToLead = async () => {
    if (!contactToConvert) return;

    try {
      const response = await convertContactToLead(contactToConvert.id);

      // Invalidate both contacts and leads cache to ensure fresh data
      apiCache.invalidatePattern('contacts-');
      apiCache.invalidatePattern('leads-');

      toast.success('Contact converted to lead successfully!');
      setShowConvertModal(false);
      setContactToConvert(null);
      navigate(`/leads`);
    } catch (error) {
      console.error('Error converting to lead:', error);
      const errorMessage = error?.response?.data?.error || error?.response?.data?.detail || 'Failed to convert contact to lead';
      toast.error(errorMessage);
    }
  };

  // Send the invite immediately on click, no confirmation step.
  const handleInviteToPortal = async (contact) => {
    if (invitingId) return;
    setInvitingId(contact.id);
    try {
      await inviteContactToPortal(contact.id);
      setInvitedIds(prev => new Set(prev).add(contact.id));
      toast.success(`Portal invite sent to ${contact.name}`);
    } catch (error) {
      console.error('Error inviting to portal:', error);
      const msg = error?.response?.data?.error || 'Failed to send portal invite';
      toast.error(msg);
    } finally {
      setInvitingId(null);
    }
  };

  // Prefer the backend's true eligible count (valid email, no portal account,
  // and NOT invited within the cooldown window) so the modal number matches what
  // the sweep will send and drops as invites go out. Fall back to the old
  // total-minus-accounts estimate only if the backend hasn't sent the field yet.


  const getContactTypeBadge = (type) => {
    const colors = {
      buyer: 'bg-blue-100 text-blue-800',
      seller: 'bg-green-100 text-green-800',
      tenant: 'bg-yellow-100 text-yellow-800',
      landlord: 'bg-[#55C5D0] bg-opacity-10 text-[#55C5D0]',
      investor: 'bg-orange-100 text-orange-800',
      general: 'bg-gray-100 text-gray-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  // Early loading return removed to allow sectional loading
  // This allows the header, filter bar, and stat cards to be visible while contacts load

  return (
    <div className="w-full font-sans py-3 bg-transparent min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
        <div>
          <h1 className="text-[32px] font-bold text-[#55C5D0] mb-1">Contacts</h1>
          <p className="text-[#6B7280] text-[15px]">Manage your contact database</p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={async () => {
              // Clear cache and reset to page 1
              apiCache.invalidatePattern('contacts-');
              if (pagination.page !== 1) {
                setPagination(prev => ({ ...prev, page: 1 }));
              } else {
                await loadContacts(true);
              }
              toast.success('Contacts refreshed');
            }}
            className="flex items-center gap-2 px-6 py-2.5 bg-white border border-[#E5E7EB] rounded-lg text-[#374151] font-bold text-[14px] hover:bg-gray-50 transition-all shadow-sm"
            title="Refresh contacts (clears cache)"
          >
            <Search className="w-4 h-4" />
            Refresh
          </button>
          {isAdmin && (
            <button
              onClick={openExportModal}
              className="flex items-center gap-2 px-6 py-2.5 bg-white border border-[#E5E7EB] rounded-lg text-[#374151] font-bold text-[14px] hover:bg-gray-50 transition-all shadow-sm"
            >
              <Upload className="w-4 h-4" />
              Export CSV
            </button>
          )}
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-6 py-2.5 bg-white border border-[#E5E7EB] rounded-lg text-[#374151] font-bold text-[14px] hover:bg-gray-50 transition-all shadow-sm"
          >
            <Upload className="w-4 h-4" />
            Import Spreadsheet
          </button>
          {/* Opens a preview first (who gets it, who is left out, the real
              greeting and email), then sends as a gradual batch that a super
              admin approves when large. Agents only reach their own contacts;
              the backend enforces that. Monday 13391839919. */}
          <button
            onClick={() => setShowInviteAllModal(true)}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2.5 bg-white border border-[#E5E7EB] rounded-lg text-[#374151] font-bold text-[14px] hover:bg-gray-50 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            title="Preview and send portal invites to contacts in the current view"
          >
            <Send className="w-4 h-4" />
            Invite All to Portal
          </button>
          <button
            onClick={() => setShowBulkAddBuyers(true)}
            className="flex items-center gap-2 px-6 py-2.5 bg-white border border-[#E5E7EB] rounded-lg text-[#374151] font-bold text-[14px] hover:bg-gray-50 transition-all shadow-sm"
            title="Paste a group of buyers and set their buying preferences in one go, with no property and nothing left in the Database section"
          >
            <UserPlus className="w-4 h-4" />
            Bulk Add Buyers
          </button>
          <button
            onClick={() => navigate('/contacts/add')}
            className="flex items-center gap-2 px-6 py-2.5 bg-[#55C5D0] text-white rounded-lg font-bold text-[14px] hover:bg-[#45B5C0] transition-all shadow-md active:scale-95"
          >
            <Plus className="w-5 h-5" />
            Add Contact
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-[#F3F4F6] rounded-2xl p-5 mb-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-center gap-4">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF] pointer-events-none" />
            <input
              type="text"
              placeholder="Search Contacts"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-white border border-[#F3F4F6] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 text-[16px] placeholder:text-[#9CA3AF] transition-all"
            />
          </div>
          <div className="w-full md:w-[220px] relative">
            <select
              value={typeFilterValue}
              onChange={(e) => applyTypeFilter(e.target.value)}
              className="w-full px-5 py-3.5 font-bold bg-[#F3F4F6] border-none rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 text-[15px]  text-[#374151] appearance-none cursor-pointer"
            >
              <option value="">All Types</option>
              <optgroup label="Contact Type">
                <option value="type:buyer">Buyer</option>
                <option value="type:seller">Seller</option>
                <option value="type:tenant">Tenant</option>
                <option value="type:landlord">Landlord</option>
                <option value="type:investor">Investor</option>
                <option value="buyer_advocates">Buyers Advocate</option>
                <option value="type:general">General</option>
              </optgroup>
              <optgroup label="Leads">
                <option value="leads_only">Leads only</option>
                <option value="contacts_only">Contacts only</option>
              </optgroup>
            </select>
            <Filter className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280] pointer-events-none" />
          </div>
          <div className="w-full md:w-[200px] relative">
            <select
              value={filters.source}
              onChange={(e) => setFilters({ ...filters, source: e.target.value })}
              className="w-full px-5 py-3.5 font-bold bg-[#F3F4F6] border-none rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 text-[15px]  text-[#374151] appearance-none cursor-pointer"
            >
              <option value="">All Sources</option>
              {Object.entries(SOURCE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <Filter className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280] pointer-events-none" />
          </div>
          <div className="w-full md:w-[200px] relative">
            <select
              value={filters.portal_status}
              onChange={(e) => setFilters({ ...filters, portal_status: e.target.value })}
              className="w-full px-5 py-3.5 font-bold bg-[#F3F4F6] border-none rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 text-[15px]  text-[#374151] appearance-none cursor-pointer"
            >
              <option value="">On and Off Portal</option>
              <option value="invited">On Portal</option>
              <option value="not_invited">Off Portal</option>
            </select>
            <Filter className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280] pointer-events-none" />
          </div>
          <div className="w-full md:w-[180px] relative">
            <select
              value={filters.ordering}
              onChange={(e) => setFilters({ ...filters, ordering: e.target.value })}
              className="w-full px-5 py-3.5 font-bold bg-[#F3F4F6] border-none rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 text-[15px]  text-[#374151] appearance-none cursor-pointer"
            >
              <option value="">Default Order</option>
              <option value="name">Name (A-Z)</option>
              <option value="-name">Name (Z-A)</option>
            </select>
            <ArrowUpDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280] pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <div className="bg-white border border-[#F3F4F6] rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow group" title="Total number of contacts in the database.">
          <p className="text-[17px] font-semibold text-[#6B7280] mb-3">Total Contacts</p>
          {loading ? (
            <div className="h-10 w-16 bg-gray-100 animate-pulse rounded" />
          ) : (
            <p className="text-[36px] font-bold text-[#111827]">
              {analytics.total_contacts || pagination.count || 0}
            </p>
          )}
        </div>
        <div className="bg-white border border-[#F3F4F6] rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow group" title="Contacts of type Buyer.">
          <p className="text-[17px] font-semibold text-[#6B7280] mb-3">Buyers</p>
          {loading ? (
            <div className="h-10 w-16 bg-gray-100 animate-pulse rounded" />
          ) : (
            <p className="text-[36px] font-bold text-[#10B981]">
              {analytics.buyers}
            </p>
          )}
        </div>
        <div className="bg-white border border-[#F3F4F6] rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow group" title="Contacts of type Seller.">
          <p className="text-[17px] font-semibold text-[#6B7280] mb-3">Sellers</p>
          {loading ? (
            <div className="h-10 w-16 bg-gray-100 animate-pulse rounded" />
          ) : (
            <p className="text-[36px] font-bold text-[#55C5D0]">
              {analytics.sellers}
            </p>
          )}
        </div>
        {/* Portal Users - the buyer-portal accounts. This card used to read
            "With Portal Account" and did nothing; the Users tab it now opens
            was moved out of the sidebar and into Contacts (Monday
            13058000755, Christian Gravias, 16 Sep 2026). */}
        <button
          type="button"
          onClick={() => navigate('/buyers')}
          className="text-left w-full bg-white border border-[#F3F4F6] rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow group cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/30"
          title="Buyer-portal login accounts. Opens the Portal Users list."
        >
          <p className="text-[17px] font-semibold text-[#6B7280] mb-3 flex items-center gap-2">
            Portal Users
            <ChevronRight className="w-4 h-4 text-[#9CA3AF] group-hover:text-[#3B82F6] transition-colors" />
          </p>
          {loading ? (
            <div className="h-10 w-16 bg-gray-100 animate-pulse rounded" />
          ) : (
            <p className="text-[36px] font-bold text-[#3B82F6]">
              {analytics.with_portal_account}
            </p>
          )}
        </button>
      </div>

      {/* Redesigned Table */}
      <div className="bg-white border border-[#F3F4F6] rounded-2xl overflow-hidden shadow-sm">
        {/* Table Header - Hidden on mobile, shown on larger screens */}
        <div className="hidden md:grid bg-[#FAFAFA] border-b border-[#F3F4F6] px-8 py-5 grid-cols-[2.5fr,1.2fr,1fr,1fr,1.5fr] items-center text-[12px] font-bold text-[#9CA3AF] uppercase tracking-widest">
          <div>CONTACT</div>
          <div className="text-center">TYPE</div>
          <div className="text-center">SOURCE</div>
          <div className="text-center">STATUS</div>
          <div className="text-center">ACTIONS</div>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-[#F3F4F6] relative">
          {loading && contacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 bg-white">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#55C5D0]"></div>
              <p className="mt-4 text-[#6B7280] font-medium">Loading contacts...</p>
            </div>
          ) : contacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-28 bg-white">
              <User className="w-20 h-20 text-[#F3F4F6] mb-5" />
              <p className="text-[18px] font-bold text-gray-900 mb-2">No contacts found</p>
              <p className="text-[15px] text-gray-400">Try adjusting your filters or search terms</p>
            </div>
          ) : (
            contacts
              .filter(contact => {
                // Filter by contact_type if selected
                if (filters.contact_type && contact.contact_type !== filters.contact_type) {
                  return false;
                }

                // Filter by source if selected
                if (filters.source && contact.source !== filters.source) {
                  return false;
                }

                // Filter by is_lead if selected
                if (filters.is_lead !== '') {
                  const isLead = filters.is_lead === 'true';
                  if (contact.is_lead !== isLead) return false;
                }

                // Filter by search
                if (filters.search) {
                  const searchLower = filters.search.toLowerCase();
                  const matchesSearch =
                    (contact.name || '').toLowerCase().includes(searchLower) ||
                    (contact.preferred_name || '').toLowerCase().includes(searchLower) ||
                    (contact.email || '').toLowerCase().includes(searchLower) ||
                    (contact.phone || '').toLowerCase().includes(searchLower);
                  if (!matchesSearch) return false;
                }

                return true;
              })
              .map((contact) => {
                return (
                  <div
                    key={contact.id}
                    onClick={async (e) => {
                      if (!isVirtualContact(contact)) {
                        navigate(`/contacts/${contact.id}`);
                        return;
                      }
                      // Virtual contact: auto-sync to CRM Contact then open profile
                      try {
                        const res = await syncVirtualContact(contact.id);
                        const synced = res?.data ?? res;
                        const contactId = synced?.id ?? synced?.pk;
                        if (contactId != null) {
                          apiCache.invalidatePattern('contacts-');
                          navigate(`/contacts/${contactId}`);
                        } else {
                          toast.error('Could not open profile.');
                        }
                      } catch (err) {
                        toast.error(err?.response?.data?.error || err?.message || 'Failed to open profile');
                      }
                    }}
                    className="px-4 sm:px-8 py-6 md:grid md:grid-cols-[2.5fr,1.2fr,1fr,1fr,1.5fr] md:items-center hover:bg-[#F9FAFB] transition-all group flex flex-col gap-4 cursor-pointer relative"
                  >
                    {/* Visual cue for clickability on hover */}
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity hidden md:block">
                      <ChevronRight className="w-5 h-5 text-[#55C5D0]" />
                    </div>
                    {/* Contact Column */}
                    <div className="flex items-center gap-5 min-w-0">
                      <div className="w-12 h-12 rounded-full bg-[#55C5D0]/10 flex items-center justify-center shrink-0 group-hover:bg-[#55C5D0]/20 transition-colors">
                        <User className="w-7 h-7 text-[#55C5D0]" />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span
                          className="text-[15px] font-bold truncate mb-0.5 text-[#55C5D0] group-hover:underline"
                        >
                          {contact.name}
                          {contact.preferred_name && (
                            <span className="ml-1.5 font-semibold text-gray-400">
                              ({contact.preferred_name})
                            </span>
                          )}
                        </span>
                        <div className="flex items-center text-[13px] text-gray-400 gap-2 truncate mb-0.5">
                          <Mail className="w-3.5 h-3.5 shrink-0" />
                          {contact.email || 'No Email'}
                        </div>
                        <div className="flex items-center text-[13px] text-gray-400 gap-2 truncate">
                          <Phone className="w-3.5 h-3.5 shrink-0" />
                          {contact.phone || "No Phone Number"}
                        </div>
                      </div>
                    </div>

                    {/* Mobile-only labels and data */}
                    <div className="md:hidden flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-gray-400 uppercase">Type</span>
                        <span className={`px-5 py-1.5 rounded-full text-[12px] font-bold inline-block capitalize ${contact.contact_type === 'buyer' ? 'bg-[#D1EDFF] text-[#007AFF]' :
                          contact.contact_type === 'seller' ? 'bg-[#ECFDF5] text-[#10B981]' :
                            'bg-[#F3F4F6] text-[#6B7280]'
                          }`}>
                          {contact.contact_type === 'buyer' ? 'Buyer' : contact.contact_type === 'buyer_advocate' ? 'Buyers Advocate' : contact.contact_type}
                        </span>
                      </div>
                        <span className="text-[14px] text-gray-400 font-medium">
                          {formatSource(contact.lead_source || contact.source)}
                        </span>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-gray-400 uppercase">Status</span>
                        <span className={`px-5 py-1.5 rounded-full text-[12px] font-bold inline-block ${contact.is_lead ? 'bg-[#D1EDFF] text-[#007AFF]' : 'bg-[#EEEEEE] text-[#9CA3AF]'
                          }`}>
                          {contact.is_lead ? 'Lead' : 'Contact'}
                        </span>
                      </div>
                    </div>

                    {/* Type Column - Desktop only */}
                    <div className="hidden md:flex md:justify-center">
                      <span className={`px-5 py-1.5 rounded-full text-[12px] font-bold inline-block capitalize ${contact.contact_type === 'buyer' ? 'bg-[#D1EDFF] text-[#007AFF]' :
                        contact.contact_type === 'seller' ? 'bg-[#ECFDF5] text-[#10B981]' :
                          'bg-[#F3F4F6] text-[#6B7280]'
                        }`}>
                        {contact.contact_type === 'buyer' ? 'Buyer' : contact.contact_type === 'buyer_advocate' ? 'Buyers Advocate' : contact.contact_type}
                      </span>
                    </div>

                    <div className="hidden md:block text-[14px] text-gray-400 font-medium text-center">
                      {formatSource(contact.lead_source || contact.source)}
                    </div>

                    {/* Status Column - Desktop only */}
                    <div className="hidden md:flex md:justify-center">
                      <span className={`px-5 py-1.5 rounded-full text-[12px] font-bold inline-block ${contact.is_lead ? 'bg-[#D1EDFF] text-[#007AFF]' : 'bg-[#EEEEEE] text-[#9CA3AF]'
                        }`}>
                        {contact.is_lead ? 'Lead' : 'Contact'}
                      </span>
                    </div>

                    {/* Actions Column, stable two-row layout, centered under
                        the ACTIONS header: an optional "Invite to Portal"
                        line on top, then the View Profile + edit + delete
                        controls on a consistent row. */}
                    <div className="flex flex-col items-center gap-2 transition-opacity" onClick={(e) => e.stopPropagation()}>

                      {/* Invite to Portal, shown only for contacts without a portal
                          account who haven't been invited within the cooldown window.
                          `invited_recently` is server-tracked so the "Invite Sent" state
                          persists across reloads and for every agent (no re-spamming). */}
                      {!isVirtualContact(contact) && !contact.has_portal_account && (
                        (invitedIds.has(contact.id) || contact.invited_recently) ? (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 text-[#10B981] text-[12px] font-bold whitespace-nowrap">
                            <Check className="w-4 h-4" />
                            Invite Sent
                          </span>
                        ) : (
                          <button
                            onClick={() => handleInviteToPortal(contact)}
                            disabled={invitingId === contact.id}
                            className="flex items-center gap-1.5 px-4 py-1.5 bg-[#55C5D0] text-white rounded-full text-[12px] font-bold hover:bg-[#45B5C0] transition-all whitespace-nowrap active:scale-95 shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                            title="Send a buyer-portal invite email"
                          >
                            <Send className="w-4 h-4" />
                            {invitingId === contact.id ? 'Sending...' : 'Invite to Portal'}
                          </button>
                        )
                      )}

                      {/* Real CRM Contact controls, one consistent row */}
                      {!isVirtualContact(contact) && (
                        <div className="flex items-center gap-2">
                          <ContactNotesButton
                            identity={{ contactId: contact.id, name: contact.name, email: contact.email, phone: contact.phone }}
                            className="px-4 py-1.5 bg-white border border-[#E5E7EB] text-[#374151] rounded-full text-[12px] font-bold hover:bg-gray-50 hover:border-amber-300 hover:text-amber-600 transition-all whitespace-nowrap active:scale-95 shadow-sm inline-flex items-center gap-1.5"
                          />
                          <button
                            onClick={(e) => { e.stopPropagation(); setPrefsContact({ contactId: contact.id, name: contact.name, email: contact.email, phone: contact.phone }); }}
                            title="Edit buying preferences"
                            className="px-4 py-1.5 bg-white border border-[#E5E7EB] text-[#374151] rounded-full text-[12px] font-bold hover:bg-gray-50 hover:border-teal-300 hover:text-teal-600 transition-all whitespace-nowrap active:scale-95 shadow-sm inline-flex items-center gap-1.5"
                          >
                            <SlidersHorizontal className="w-3.5 h-3.5" /> Preferences
                          </button>
                          <button
                            onClick={() => navigate(`/contacts/${contact.id}`)}
                            className="px-4 py-1.5 bg-white border border-[#E5E7EB] text-[#374151] rounded-full text-[12px] font-bold hover:bg-gray-50 transition-all whitespace-nowrap active:scale-95 shadow-sm"
                          >
                            View Profile
                          </button>
                          <button
                            onClick={() => navigate(`/contacts/edit/${contact.id}`)}
                            className="p-2 text-[#3B82F6] hover:bg-[#3B82F6]/10 rounded-lg transition-all"
                            title="Edit Contact"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(contact.id)}
                            className="p-2 text-[#EF4444] hover:bg-[#EF4444]/10 rounded-lg transition-all"
                            title="Delete Contact"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      )}

                      {/* Virtual (Portal) contact buttons, sync to real contact then open profile */}
                      {isVirtualContact(contact) && (
                        <div className="flex items-center gap-2">
                        <ContactNotesButton
                          identity={{ email: contact.email, name: contact.name, phone: contact.phone }}
                          className="px-4 py-1.5 bg-white border border-[#E5E7EB] text-[#374151] rounded-full text-[12px] font-bold hover:bg-gray-50 hover:border-amber-300 hover:text-amber-600 transition-all whitespace-nowrap active:scale-95 shadow-sm inline-flex items-center gap-1.5"
                        />
                        <button
                          onClick={(e) => { e.stopPropagation(); setPrefsContact({ email: contact.email, name: contact.name, phone: contact.phone }); }}
                          title="Edit buying preferences"
                          className="px-4 py-1.5 bg-white border border-[#E5E7EB] text-[#374151] rounded-full text-[12px] font-bold hover:bg-gray-50 hover:border-teal-300 hover:text-teal-600 transition-all whitespace-nowrap active:scale-95 shadow-sm inline-flex items-center gap-1.5"
                        >
                          <SlidersHorizontal className="w-3.5 h-3.5" /> Preferences
                        </button>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const res = await syncVirtualContact(contact.id);
                              const synced = res?.data ?? res;
                              const contactId = synced?.id ?? synced?.pk;
                              if (contactId != null) {
                                apiCache.invalidatePattern('contacts-');
                                navigate(`/contacts/${contactId}`);
                              } else {
                                toast.error('Could not open profile.');
                              }
                            } catch (err) {
                              toast.error(err?.response?.data?.error || 'Failed to open profile');
                            }
                          }}
                          className="px-4 py-1.5 bg-white border border-[#E5E7EB] text-[#374151] rounded-full text-[12px] font-bold hover:bg-gray-50 transition-all whitespace-nowrap active:scale-95 shadow-sm"
                        >
                          Open Profile
                        </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
          )}

          {/* Invisible target for IntersectionObserver */}
          <div ref={observerTarget} className="h-20 w-full flex flex-col items-center justify-center border-t border-[#F3F4F6] bg-white">
            {(loading || isRefreshing) && contacts.length > 0 && (
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#55C5D0]"></div>
                <span className="text-[#6B7280] text-[14px] font-medium">
                  {loading ? 'Loading contacts...' : 'Loading more contacts...'}
                </span>
              </div>
            )}
            {!loading && !isRefreshing && pagination.page >= pagination.total_pages && contacts.length > 0 && (
              <div className="flex flex-col items-center gap-1">
                <p className="text-[#9CA3AF] text-[13px] font-medium italic">You've reached the end of the list</p>
                <p className="text-[#D1D5DB] text-[11px] uppercase tracking-wider font-bold">Total {pagination.count} contacts</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Export Options Modal, pick which contacts to export */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="w-full max-w-lg bg-white rounded-xl shadow-lg p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Export Contacts to CSV</h3>
              <button onClick={() => setShowExportModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Choose which contacts to include. Leave a filter on <span className="font-medium">All</span> to include everything.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Type</label>
                <select
                  value={exportFilters.contact_type}
                  onChange={(e) => setExportFilters({ ...exportFilters, contact_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                >
                  <option value="">All Types</option>
                  <option value="buyer">Buyer</option>
                  <option value="seller">Seller</option>
                  <option value="tenant">Tenant</option>
                  <option value="landlord">Landlord</option>
                  <option value="investor">Investor</option>
                  <option value="general">General</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <select
                  value={exportFilters.source}
                  onChange={(e) => setExportFilters({ ...exportFilters, source: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                >
                  <option value="">All Sources</option>
                  {Object.entries(SOURCE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={exportFilters.is_lead}
                  onChange={(e) => setExportFilters({ ...exportFilters, is_lead: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                >
                  <option value="">All Contacts &amp; Leads</option>
                  <option value="true">Leads only</option>
                  <option value="false">Contacts only</option>
                </select>
              </div>

              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={exportFilters.is_buyer_advocate === 'true'}
                  onChange={(e) => setExportFilters({ ...exportFilters, is_buyer_advocate: e.target.checked ? 'true' : '' })}
                />
                <span className="text-sm text-gray-700">Buyer Advocates only</span>
              </label>
            </div>

            <div className="flex justify-between items-center gap-3 mt-6">
              <button
                type="button"
                onClick={() => setExportFilters({ contact_type: '', source: '', is_lead: '', is_buyer_advocate: '' })}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Reset filters
              </button>
              <div className="flex gap-3">
                <button onClick={() => setShowExportModal(false)} className="px-4 py-2 bg-gray-100 rounded-md">Cancel</button>
                <button
                  disabled={exporting}
                  onClick={handleExport}
                  className="flex items-center gap-2 px-4 py-2 bg-[#55C5D0] text-white rounded-md hover:bg-[#3bb3be] disabled:opacity-50"
                >
                  <Upload className="w-4 h-4" />
                  {exporting ? 'Exporting…' : 'Export CSV'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {
        showImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
            <div className="w-full max-w-2xl bg-white rounded-xl shadow-lg p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  {importPreview ? 'Review Before Importing' : 'Import Contacts from a Spreadsheet'}
                </h3>
                <button onClick={resetImportModal} className="text-gray-500 hover:text-gray-700">✕</button>
              </div>

              {/* ---------- STEP 1: choose file + options ---------- */}
              {!importPreview && (
                <>
                  <div className="space-y-4">
                    <div className="bg-[#F0FBFC] border border-[#B7E6EB] rounded-md p-3 flex items-start justify-between gap-3">
                      <div className="text-sm text-gray-700">
                        <p className="font-medium">Need a template?</p>
                        <p className="text-gray-600 text-xs mt-0.5">Download a sample with all supported columns and one example row. Opens directly in Excel or Google Sheets.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const headers = [
                            'First Name', 'Last Name', 'Email', 'Mobile', 'Alternate Phone',
                            'Company', 'Position',
                            'Address', 'Suburb', 'State', 'Postcode',
                            'Contact Type', 'Source', 'Tags', 'Vault ID',
                            'Buyer Suburbs', 'Budget Min', 'Budget Max',
                          ];
                          const example = [
                            'John', 'Smith', 'john.smith@example.com', '0412345678', '',
                            'Acme Realty', 'Buyer',
                            '12 Example St', 'Carlton', 'VIC', '3053',
                            'buyer', 'website', 'VIP,Investor', '',
                            'Northcote,Heidelberg', '$1,000,000', '$1,500,000',
                          ];
                          const escape = (v) => {
                            const s = String(v ?? '');
                            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                          };
                          const csv = [headers, example].map((r) => r.map(escape).join(',')).join('\n');
                          // Prepend UTF-8 BOM so Excel opens it correctly
                          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'contacts_import_template.csv';
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        }}
                        className="shrink-0 px-3 py-1.5 text-sm border border-[#55C5D0] text-[#1f8a96] bg-white rounded-md hover:bg-[#E7F7F9]"
                      >
                        Download Template
                      </button>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Spreadsheet File</label>
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={(e) => { setImportData({ ...importData, file: e.target.files?.[0] || null }); setImportPreview(null); }}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Accepts Excel (.xlsx, .xls) or CSV. Required: at least Email or Mobile. Contact Type: buyer, seller, tenant, landlord, investor, general. Source: website, referral, ofi, social_media, cold_call, walk_in, buyer_portal, other.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Duplicate Handling</label>
                        <select
                          value={importData.duplicate_strategy}
                          onChange={(e) => setImportData({ ...importData, duplicate_strategy: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                        >
                          <option value="skip">Skip Existing</option>
                          <option value="update">Update Existing</option>
                          <option value="create">Always Create New</option>
                        </select>
                      </div>
                      <div className="flex items-end">
                        <label className="inline-flex items-center gap-2">
                          <input type="checkbox" checked={importData.assign_to_me} onChange={(e) => setImportData({ ...importData, assign_to_me: e.target.checked })} />
                          <span>Assign imported contacts to me</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 mt-6">
                    <button onClick={resetImportModal} className="px-4 py-2 bg-gray-100 rounded-md">Cancel</button>
                    <button
                      disabled={analyzing || !importData.file}
                      onClick={handleAnalyzeImport}
                      className="px-4 py-2 bg-[#55C5D0] text-white rounded-md hover:bg-[#3bb3be] disabled:opacity-50"
                    >
                      {analyzing ? 'Analyzing…' : 'Analyze File'}
                    </button>
                  </div>
                  <div className="mt-4">
                    <RecentContactImports onUndone={() => { apiCache.invalidatePattern('contacts-'); loadContacts(true); }} />
                  </div>
                </>
              )}

              {/* ---------- STEP 2: review the dry-run preview ---------- */}
              {importPreview && (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="rounded-lg border border-[#BBF7D0] bg-[#F0FDF4] p-3 text-center">
                      <p className="text-2xl font-bold text-[#16A34A]">{importPreview.counts?.new ?? 0}</p>
                      <p className="text-xs font-medium text-gray-600 mt-0.5">New contacts</p>
                    </div>
                    <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] p-3 text-center">
                      <p className="text-2xl font-bold text-[#D97706]">{importPreview.counts?.existing ?? 0}</p>
                      <p className="text-xs font-medium text-gray-600 mt-0.5">Already in Contacts</p>
                    </div>
                    <div className="rounded-lg border border-[#FECACA] bg-[#FEF2F2] p-3 text-center">
                      <p className="text-2xl font-bold text-[#DC2626]">{importPreview.counts?.problems ?? 0}</p>
                      <p className="text-xs font-medium text-gray-600 mt-0.5">Rows with problems</p>
                    </div>
                  </div>

                  {/* Problem rows */}
                  <div className="space-y-3 mb-4">
                    <ListKindPicker
                      options={importPreview.list_kinds || []}
                      value={importData.list_kind}
                      onChange={(k) => setImportData({ ...importData, list_kind: k })}
                    />
                    <DataCheckPanel quality={importPreview.quality} />
                  </div>

                  {importPreview.problems?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-[#DC2626] mb-1.5">Problems, these rows will be skipped</p>
                      <div className="border border-[#FECACA] rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-[#FEF2F2] text-gray-600 sticky top-0">
                            <tr><th className="text-left px-3 py-1.5 font-semibold">Row</th><th className="text-left px-3 py-1.5 font-semibold">Name / Contact</th><th className="text-left px-3 py-1.5 font-semibold">Reason</th></tr>
                          </thead>
                          <tbody>
                            {importPreview.problems.map((p, i) => (
                              <tr key={i} className="border-t border-[#FECACA]/60">
                                <td className="px-3 py-1.5 text-gray-500">{p.row}</td>
                                <td className="px-3 py-1.5 text-gray-800">{p.name || p.email || p.phone || '—'}</td>
                                <td className="px-3 py-1.5 text-[#DC2626]">{p.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Existing rows */}
                  {importPreview.existing?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-[#D97706] mb-1.5">Already in Contacts</p>
                      <div className="border border-[#FDE68A] rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-[#FFFBEB] text-gray-600 sticky top-0">
                            <tr><th className="text-left px-3 py-1.5 font-semibold">Row</th><th className="text-left px-3 py-1.5 font-semibold">Name</th><th className="text-left px-3 py-1.5 font-semibold">Email / Phone</th><th className="text-left px-3 py-1.5 font-semibold">What happens</th></tr>
                          </thead>
                          <tbody>
                            {importPreview.existing.map((r, i) => (
                              <tr key={i} className="border-t border-[#FDE68A]/60">
                                <td className="px-3 py-1.5 text-gray-500">{r.row}</td>
                                <td className="px-3 py-1.5 text-gray-800">{r.name || '—'}</td>
                                <td className="px-3 py-1.5 text-gray-500">{r.email || r.phone || '—'}</td>
                                <td className="px-3 py-1.5 text-[#D97706]">{r.action}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* New rows */}
                  {importPreview.new?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-[#16A34A] mb-1.5">New contacts to be added</p>
                      <div className="border border-[#BBF7D0] rounded-lg overflow-hidden max-h-44 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-[#F0FDF4] text-gray-600 sticky top-0">
                            <tr><th className="text-left px-3 py-1.5 font-semibold">Row</th><th className="text-left px-3 py-1.5 font-semibold">Name</th><th className="text-left px-3 py-1.5 font-semibold">Email / Phone</th><th className="text-left px-3 py-1.5 font-semibold">Type</th></tr>
                          </thead>
                          <tbody>
                            {importPreview.new.map((r, i) => (
                              <tr key={i} className="border-t border-[#BBF7D0]/60">
                                <td className="px-3 py-1.5 text-gray-500">{r.row}</td>
                                <td className="px-3 py-1.5 text-gray-800">{r.name || '—'}</td>
                                <td className="px-3 py-1.5 text-gray-500">{r.email || r.phone || '—'}</td>
                                <td className="px-3 py-1.5 text-gray-600 capitalize">{r.contact_type}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {importPreview.counts?.total === 0 && (
                    <p className="text-sm text-gray-500 py-6 text-center">No usable rows were found in this file.</p>
                  )}

                  <div className="flex justify-between items-center gap-3 mt-6">
                    <button
                      onClick={() => setImportPreview(null)}
                      className="px-4 py-2 bg-gray-100 rounded-md text-gray-700 hover:bg-gray-200"
                    >
                      ← Back
                    </button>
                    <div className="flex gap-3">
                      <button onClick={resetImportModal} className="px-4 py-2 bg-gray-100 rounded-md">Cancel</button>
                      <button
                        disabled={
                          importing
                          || (importPreview.counts?.new === 0 && importPreview.counts?.existing === 0)
                          || !!importPreview.quality?.blocking
                          || (importPreview.list_kinds?.length > 0 && !importData.list_kind)
                        }
                        title={importPreview.quality?.blocking ? 'Fix the problems in red in the file first'
                          : !importData.list_kind ? 'Say what this list is first' : ''}
                        onClick={handleConfirmImport}
                        className="px-4 py-2 bg-[#55C5D0] text-white rounded-md hover:bg-[#3bb3be] disabled:opacity-50"
                      >
                        {importing
                          ? 'Importing…'
                          : `Confirm & Import${importPreview.counts?.new ? ` (${importPreview.counts.new} new)` : ''}`}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      {
        showDeleteModal && contactToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Are you sure you want to delete this contact?
              </h3>
              <p className="text-gray-600 mb-6">
                <span className="font-medium">{contactToDelete.name}</span> will be removed from your contacts. An admin can restore them from Trash within 90 days.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setContactToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Convert to Lead Confirmation Modal */}
      {
        showConvertModal && contactToConvert && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Convert Contact to Lead
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to convert <span className="font-medium">{contactToConvert.name}</span> to a lead?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowConvertModal(false);
                    setContactToConvert(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmConvertToLead}
                  className="px-4 py-2 text-white bg-[#55C5D0] rounded-md hover:bg-[#45B5C0]"
                >
                  Convert
                </button>
              </div>
            </div>
          </div>
        )
      }

      {showInviteAllModal && (
        <InviteAllDialog filters={filters} onClose={() => setShowInviteAllModal(false)} />
      )}

      {showBulkAddBuyers && (
        <BulkAddBuyersModal
          onClose={() => setShowBulkAddBuyers(false)}
          onAdded={() => loadContacts(true)}
        />
      )}

      {prefsContact && (
        <BuyerPreferencesModal
          identity={prefsContact}
          title={prefsContact.name}
          onClose={() => setPrefsContact(null)}
        />
      )}

    </div >
  );
};

export default ContactList;

